
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TeacherLayout from "@/components/TeacherLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock,
  ArrowLeft,
  Save
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Student {
  id: string;
  name: string;
  email: string;
}

interface AttendanceRecord {
  student_id: string;
  status: 'present' | 'absent' | 'late';
  remarks: string;
}

interface AttendanceHistory {
  id: string;
  class_id: string;
  class_name: string;
  date: string;
  marked_time: string | null;
  total_students: number;
  present_count: number;
  absent_count: number;
  late_count: number;
  formatted_counts: string | null;
  student_attendance: Array<{
    student_id: string;
    student_name: string;
    status: string;
    remarks: string;
  }>;
  created_at: string;
}

const AttendancePage = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [students, setStudents] = useState<Student[]>([]);
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceHistory[]>([]);
  const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [className, setClassName] = useState<string>('');

  useEffect(() => {
    if (classId) {
      fetchStudents();
      fetchAttendanceHistory();
    }
  }, [classId]);

  const fetchStudents = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl(`/teacher/classes/${classId}/students`), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
        setClassName(data.class_name || '');
        
        const initialAttendance = (data.students || []).map((student: Student) => ({
          student_id: student.id,
          status: 'present' as const,
          remarks: ''
        }));
        setAttendanceData(initialAttendance);
      }
    } catch (error) {
      console.error('Students fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to load students",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAttendanceHistory = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl(`/teacher/classes/${classId}/attendance/history`), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAttendanceHistory(data);
      }
    } catch (error) {
      console.error('Attendance history fetch error:', error);
    }
  };

  const updateAttendance = (studentId: string, status: 'present' | 'absent' | 'late') => {
    setAttendanceData(prev => 
      prev.map(record => 
        record.student_id === studentId 
          ? { ...record, status }
          : record
      )
    );
  };

  const submitAttendance = async () => {
    if (!classId) return;
    
    setIsSubmitting(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;

      const response = await fetch(createApiUrl(`/teacher/attendance/${classId}`), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: new Date().toISOString().split('T')[0],
          students: attendanceData
        }),
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Attendance has been recorded successfully"
        });
        fetchAttendanceHistory();
      } else {
        const errorData = await response.json();
        toast({
          title: "Info",
          description: errorData.message || "Attendance updated",
        });
      }
    } catch (error) {
      console.error('Attendance submission error:', error);
      toast({
        title: "Error",
        description: "Failed to record attendance",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <TeacherLayout>
        <div className="flex items-center justify-center min-h-[300px] sm:min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading attendance data...</p>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  const todaysDate = new Date().toLocaleDateString();
  const presentCount = attendanceData.filter(record => record.status === 'present').length;
  const absentCount = attendanceData.filter(record => record.status === 'absent').length;
  const lateCount = attendanceData.filter(record => record.status === 'late').length;

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/teacher/attendance')}
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">{className}</h1>
              <p className="text-gray-600 text-sm sm:text-base">Mark attendance for {todaysDate}</p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{students.length}</div>
              <p className="text-xs text-gray-600">Total</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-green-600">{presentCount}</div>
              <p className="text-xs text-gray-600">Present</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-red-600">{absentCount}</div>
              <p className="text-xs text-gray-600">Absent</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-orange-600">{lateCount}</div>
              <p className="text-xs text-gray-600">Late</p>
            </CardContent>
          </Card>
        </div>

        {/* Mark Attendance */}
        <Card>
          <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4">
            <div>
              <CardTitle className="text-base sm:text-lg">Mark Today's Attendance</CardTitle>
              <p className="text-xs sm:text-sm text-gray-600">Click on status buttons to mark attendance</p>
            </div>
            <Button 
              onClick={submitAttendance}
              disabled={isSubmitting}
              className="w-full sm:w-auto mt-3 sm:mt-0 text-xs sm:text-sm"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Saving...' : 'Save Attendance'}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {students.map((student) => {
                const studentAttendance = attendanceData.find(record => record.student_id === student.id);
                return (
                  <div key={student.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 border rounded-lg gap-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm sm:text-base truncate">{student.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{student.email}</p>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        size="sm"
                        variant={studentAttendance?.status === 'present' ? 'default' : 'outline'}
                        onClick={() => updateAttendance(student.id, 'present')}
                        className="flex-1 sm:flex-none text-xs"
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Present
                      </Button>
                      <Button
                        size="sm"
                        variant={studentAttendance?.status === 'absent' ? 'destructive' : 'outline'}
                        onClick={() => updateAttendance(student.id, 'absent')}
                        className="flex-1 sm:flex-none text-xs"
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Absent
                      </Button>
                      <Button
                        size="sm"
                        variant={studentAttendance?.status === 'late' ? 'secondary' : 'outline'}
                        onClick={() => updateAttendance(student.id, 'late')}
                        className="flex-1 sm:flex-none text-xs"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Late
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Attendance History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Attendance History</CardTitle>
            <p className="text-xs sm:text-sm text-gray-600">Previous attendance records for this class</p>
          </CardHeader>
          <CardContent>
            {attendanceHistory.length > 0 ? (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs sm:text-sm">Date</TableHead>
                        <TableHead className="text-xs sm:text-sm">Present</TableHead>
                        <TableHead className="text-xs sm:text-sm">Absent</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden sm:table-cell">Late</TableHead>
                        <TableHead className="text-xs sm:text-sm hidden md:table-cell">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attendanceHistory.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="text-xs sm:text-sm">
                            {new Date(record.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                              {record.present_count}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="bg-red-100 text-red-800 text-xs">
                              {record.absent_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            <Badge variant="secondary" className="bg-orange-100 text-orange-800 text-xs">
                              {record.late_count}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs sm:text-sm hidden md:table-cell">
                            {record.total_students}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No History Available</h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Attendance history will appear here once you start marking attendance.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TeacherLayout>
  );
};

export default AttendancePage;
